/**
 * ActivityIndicator Component
 *
 * A loading spinner component with size and color variants.
 *
 * @example Basic usage
 * ```tsx
 * <ActivityIndicator />
 * ```
 *
 * @example Different sizes
 * ```tsx
 * <ActivityIndicator size="small" />
 * <ActivityIndicator size="large" />
 * ```
 *
 * @example Color variants
 * ```tsx
 * <ActivityIndicator variant="primary" />
 * <ActivityIndicator variant="secondary" />
 * <ActivityIndicator variant="neutral" />
 * <ActivityIndicator variant="inherit" />
 * ```
 */

import React from 'react';
import { cn } from '../../utils/cn';
import styles from './activity-indicator.module.css';
import type { ActivityIndicatorProps } from './activity-indicator.types';

export const ActivityIndicator: React.FC<ActivityIndicatorProps> = ({
    animating = true,
    size = 'medium',
    variant = 'primary',
    color,
    className,
    showIf = true,
    ...props
}) => {
    if (!animating || !showIf) return null;

    const containerClasses = cn(
        styles.activityIndicator,

        // Size variants
        size === 'small' && styles.sizeSmall,
        size === 'medium' && styles.sizeMedium,
        size === 'large' && styles.sizeLarge,

        // Color variants
        !color && variant === 'primary' && styles.variantPrimary,
        !color && variant === 'secondary' && styles.variantSecondary,
        !color && variant === 'neutral' && styles.variantNeutral,
        !color && variant === 'inherit' && styles.variantInherit,

        // Custom className
        className
    );

    // Use custom color or CSS variable from variant
    const strokeColor = color || 'var(--spinner-color)';

    return (
        <div
            className={containerClasses}
            data-testid="activity-indicator"
            role="status"
            aria-label="Loading"
            {...props}
        >
            <svg className={styles.spinner} viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Background track */}
                <circle
                    className={styles.spinnerTrack}
                    cx="25"
                    cy="25"
                    r="20"
                    stroke={strokeColor}
                    strokeWidth="4"
                    fill="none"
                />
                {/* Animated arc - Material Design style */}
                <circle
                    className={styles.spinnerArc}
                    cx="25"
                    cy="25"
                    r="20"
                    stroke={strokeColor}
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray="1, 200"
                    strokeDashoffset="0"
                />
            </svg>
        </div>
    );
};
